
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IGallery, IProfileWebsite, ITournamentEditionMatch, OmneHostService, SanitizedResponse } from "../../../../services";
import { Utils } from "../../../../utils";
import { alertDialog, Classname, FileHelper, fileInputBuildFileInputPreview, FileInputPreviewType } from "@rivtn/noseur";
import { i18nManager } from "../../../../i18n";
import { buildGalleryView } from "./Gallery";
import { buildMatchView } from "./Schedule";

export interface HomeProps {
    basePath: string;
    publicId: string;
    website: IProfileWebsite;
}

function Home(props: HomeProps) {

    const { basePath, website, publicId } = props;
    const omnehostService = OmneHostService.getInstance();
    const displayNameParts = website.display_name.split(" ");
    const [gallery, setGallery] = React.useState<IGallery[]>();
    const [matches, setMatches] = React.useState<ApiResponseData<ITournamentEditionMatch>>();

    const ACHIEVEMENTS = [
        { year: 2025, title: "Champions League Winner", icon: "fa fa-trophy", desc: "Led the team with 8 goals and 11 assists throughout the tournament." },
        { year: 2025, title: "La Liga Elite MVP", icon: "fa fa-star", desc: "Named the league's most valuable player for the second consecutive season." },
        { year: 2024, title: "Golden Boot", icon: "fa fa-shoe-prints", desc: "Finished as top scorer with 31 goals in a single league season." },
        { year: 2024, title: "Copa Elite Champion", icon: "fa fa-medal", desc: "Scored the winning penalty in the final against rivals Dynamo United." },
        { year: 2023, title: "World's Best Player", icon: "fa fa-globe", desc: "Awarded the prestigious Balondor prize by international football federation." },
        { year: 2023, title: "International Cup Winner", icon: "fa fa-medal", desc: "Guided Brazil to glory in the summer international tournament." },
        { year: 2022, title: "U23 World Cup Winner", icon: "fa fa-award", desc: "Scored a hat-trick in the final to become tournament top scorer." },
        { year: 2021, title: "Best Young Player", icon: "fa fa-certificate", desc: "Recognized as the world's best player under 21." },
    ];

    React.useEffect(fetchInformation, []);

    return (<>
        {/* HERO */}
        <section className={styles["hero"]}>
            <div className={styles["hero-bg"]} />
            <div className={styles["hero-grid"]} />
            <div className={styles["hero-content"]}>
                <div>
                    <div className={styles["hero-tag"]}><i className="fa fa-futbol" /> Official Website</div>
                    <h1 className={styles["hero-name"]}>{displayNameParts.slice(0, displayNameParts.length - 1).join(" ")}{(displayNameParts?.length > 0) && (<><br /><em>{displayNameParts[displayNameParts.length - 1]}</em></>)}</h1>
                    <p className={styles["hero-bio"]}>{website.profile_detail}</p>
                    <div className={styles["hero-stats"]}>
                        {(website.home_settings.show_stats && !!website.extra_data?.stats) && (Object.entries(website.extra_data?.stats).map(([k, v]) => (
                            <div key={k} className={styles["h-stat"]}>
                                <span className={styles["h-stat-val"]}>{v as number}</span>
                                <span className={styles["h-stat-lbl"]}>{k.replaceAll("_", " ")}</span>
                            </div>
                        )))}
                    </div>
                    <div className={styles["hero-cta"]}>
                        {(website.website_pages?.MERCH?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/merch`} className={styles["btn-primary"]} style={{ textDecoration: "none" }}>Shop Merchandise</ReactRouterDom.NavLink>)}
                        {(website.website_pages?.GALLERY?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/gallery`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>View Gallery</ReactRouterDom.NavLink>)}
                    </div>
                </div>
                {(website.media?.PASSPORT?.length) && (<div className={styles["hero-player-wrap"]}>
                    <div className={styles["hero-player-glow"]} />
                    <img className={styles["hero-player-img"]} src={website.media?.PASSPORT?.[website.extra_data?.media?.PASSPORT?.active ?? 0]} alt={website.display_name} />
                    {(!!website.member?.age) && (<div className={styles["hero-player-badge"]}>
                        <span className={styles["hero-player-badge-num"]}>{website.member?.age}</span>
                        <span className={styles["hero-player-badge-lbl"]}>Age</span>
                    </div>)}
                </div>)}
            </div>
            <div className={styles["hero-num"]}>{website.display_name}</div>
        </section>

        {/* SPONSORS */}
        {(website.home_settings?.show_sponsors && website.sponsors?.length) && (<div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h2 className={styles["sec-title"]}>My <em>Sponsors</em></h2>
            <p className={styles["sec-sub"]}>Proud partners supporting my journey</p>
            <div className={styles["sponsor-row"]}>
                {website.sponsors.map((s) => (
                    <div key={s} className={styles["sponsor-chip"]}>
                        <img alt={s} src={s} />
                    </div>
                ))}
            </div>
        </div>)}

        {/* RECENT ACHIEVEMENTS */}
        {(website.website_pages?.ACHIEVEMENTS?.visible) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
            <div className={styles["sec-line"]} />
            <h2 className={styles["sec-title"]}>Recent <em>Achievements</em></h2>
            <p className={styles["sec-sub"]}>A decorated career defined by excellence</p>
            <div className={styles["ach-strip"]}>
                {ACHIEVEMENTS.slice(0, 5).map((a) => (
                    <div key={a.title} className={styles["ach-chip"]}>
                        <div className={styles["ach-icon"]}><i className={a.icon} /></div>
                        <div>
                            <div className={styles["ach-year"]}>{a.year}</div>
                            <div className={styles["ach-chip-title"]}>{a.title}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>)}

        {/* UPCOMING MATCHES */}
        {(website.website_pages?.SCHEDULE?.visible) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
            <div className={styles["sec-line"]} />
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                <div>
                    <h2 className={styles["sec-title"]}>My <em>Matches</em></h2>
                    <p className={styles["sec-sub"]}>Recent fixtures on the calendar</p>
                </div>
                <ReactRouterDom.NavLink end to={`/${basePath}/schedule`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >Full Schedule <i className="fa fa-arrow-right" /></ReactRouterDom.NavLink>
            </div>
            <div className={styles["matches-brief"]}>
                {matches?.content!.map((t) => buildMatchView(publicId, website, t))}
            </div>
        </div>)}

        {/* SOCIAL */}
        {(Object.keys(website.member.social_accounts ?? {}).length > 0) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
            <div className={styles["sec-line"]} />
            <h2 className={styles["sec-title"]}>Social <em>Networks</em></h2>
            <p className={styles["sec-sub"]}>Follow the journey</p>
            <div className={styles["social-grid"]}>
                {Object.entries(website.member.social_accounts ?? {}).map(([social, username]) => (<ReactRouterDom.NavLink target="_blank" key={social} to={Utils.getSocialLink(social, username)} className={styles["social-card"]}>
                    <i className={Classname.build(styles["social-icon"], Utils.getSocialIcon(social))} />
                    <div className={styles["social-name"]}>{social}</div>
                    <div className={styles["social-handle"]}>{username}</div>
                </ReactRouterDom.NavLink>))}
            </div>
        </div>)}

        {/* TOP GALLERY */}
        {(website.website_pages?.GALLERY?.visible) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
            <div className={styles["sec-line"]} />
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                <div>
                    <h2 className={styles["sec-title"]}>Top <em>Gallery</em></h2>
                    <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>Behind the lens moments</p>
                </div>
                <ReactRouterDom.NavLink end to={`/${basePath}/gallery`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >View All <i className="fa fa-arrow-right" /></ReactRouterDom.NavLink>
            </div>
            <div className={`${styles["gallery-grid"]} ${styles["home-grid"]}`}>
                {gallery?.map(buildGalleryView)}
            </div>
        </div>)}
    </>);

    function fetchInformation() {
        omnehostService.queryMemberGallery(props.website.member.username, {
            size: 10,
        }).then(({ content }: SanitizedResponse<IGallery>) => {
            setGallery(content);
        }).catch(BaseService.reportError);
        omnehostService.queryMemberSchedule(website.member.username, {
            size: 10,
            sort: ["start_date,ASC", "status,ASC"],
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            setMatches(apiResponse.data!);
        }).catch(BaseService.reportError);
    }
}

export default Home;